/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-underscore-dangle */
import type { ComponentType, LazyExoticComponent } from 'react';
import { Fragment, lazy } from 'react';
import * as z from 'zod';

import WaveWrapper from '@/components/layout/WaveWrapper/WaveWrapper';
import type { WaveWrapperType } from '@/types/WaveWrapperType';
import cn from '@/utils/cn';
import { mapStrapiLayoutToWaveWrapper } from '@/utils/blocks/utils';
import { BlockNameEnum } from '@/enum/BlockNameEnum';

// ZOD
import { HeroSectionZod } from '@/components/blocks/HeroSection/HeroSectionType';
import { LogoClientsZod } from '@/components/blocks/LogoClients/LogoClientsType';
import { TextMediaZod } from '@/components/blocks/TextMedia/TextMediaType';
import { TextBubblesZod } from '@/components/blocks/TextBubbles/TextBubblesType';
import { TextAccordionZod } from '@/components/blocks/TextAccordion/TextAccordionType';
import { NumberedListZod } from '@/components/blocks/NumberedList/NumberedListType';
import { FAQZod } from '@/components/blocks/FAQ/FAQType';
import { ActivitiesZod } from '@/components/blocks/Activities/ActiviesType';
import { ClientOverviewZod } from '@/components/blocks/ClientOverview/ClientOverviewType';
import { FramedPicturesZod } from '@/components/blocks/FramedPictures/FramedPicturesType';
import { CustomerCasesZod } from '@/components/blocks/CustomerCases/CustomerCasesType';
import { DoudouOverviewZod } from '@/components/blocks/DoudouOverview/DoudouOverviewType';
import { MockupZod } from '@/components/blocks/Mockup/MockupType';
import { OffersZod } from '@/components/blocks/Offers/OffersType';
import { QuotationZod } from '@/components/blocks/Quotation/QuotationType';
import { PressZod } from '@/components/blocks/Press/PressType';
import { WysiwygZod } from '@/components/blocks/Wysiwyg/WysiwygType';
import { JobDetailsZod } from '@/components/blocks/JobDetails/JobDetailsType';
import { OfferFormZod } from '@/components/blocks/OfferForm/OfferFormType';
import { JobApplicationFormZod } from '@/components/blocks/JobApplicationForm/JobApplicationFormType';
import { DualImageTextSectionZod } from '@/components/blocks/DualImageTextSection/DualImageTextSectionType';
import { IllustrationZod } from '@/components/blocks/Illustration/IllustrationType';
import type { FramedPicturesProps } from '@/components/blocks/FramedPictures/FramedPictures';

// Dynamic imports for components
const HeroSection = lazy(() => import('@/components/blocks/HeroSection/HeroSection'));
const TextBubbles = lazy(() => import('@/components/blocks/TextBubbles/TextBubbles'));
const LogoClients = lazy(() => import('@/components/blocks/LogoClients/LogoClients'));
const TextMedia = lazy(() => import('@/components/blocks/TextMedia/TextMedia'));
const TextAccordion = lazy(() => import('@/components/blocks/TextAccordion/TextAccordion'));
const NumberedList = lazy(() => import('@/components/blocks/NumberedList/NumberedList'));
const FAQ = lazy(() => import('@/components/blocks/FAQ/FAQ'));
const Activities = lazy(() => import('@/components/blocks/Activities/Activities'));
const ClientOverview = lazy(() => import('@/components/blocks/ClientOverview/ClientOverview'));
const FramedPictures = lazy(() => import('@/components/blocks/FramedPictures/FramedPictures')) as LazyExoticComponent<
  ComponentType<FramedPicturesProps>
>;
const CustomerCases = lazy(() => import('@/components/blocks/CustomerCases/CustomerCases'));
const DoudouOverview = lazy(() => import('@/components/blocks/DoudouOverview/DoudouOverview'));
const Mockup = lazy(() => import('@/components/blocks/Mockup/Mockup'));
const Offers = lazy(() => import('@/components/blocks/Offers/Offers'));
const Quotation = lazy(() => import('@/components/blocks/Quotation/Quotation'));
const Press = lazy(() => import('@/components/blocks/Press/Press'));
const Wysiwyg = lazy(() => import('@/components/blocks/Wysiwyg/Wysiwyg'));
const JobDetails = lazy(() => import('@/components/blocks/JobDetails/JobDetails'));
const OfferForm = lazy(() => import('@/components/blocks/OfferForm/OfferForm'));
const JobApplicationForm = lazy(() => import('@/components/blocks/JobApplicationForm/JobApplicationForm'));
const DualImageTextSection = lazy(() => import('@/components/blocks/DualImageTextSection/DualImageTextSection'));
const Illustration = lazy(() => import('@/components/blocks/Illustration/Illustration'));

export const blocksSchemaZod = z.discriminatedUnion('__component', [
  HeroSectionZod,
  TextBubblesZod,
  LogoClientsZod,
  TextMediaZod,
  TextAccordionZod,
  NumberedListZod,
  FAQZod,
  ActivitiesZod,
  ClientOverviewZod,
  FramedPicturesZod,
  CustomerCasesZod,
  DoudouOverviewZod,
  MockupZod,
  OffersZod,
  QuotationZod,
  PressZod,
  WysiwygZod,
  JobDetailsZod,
  OfferFormZod,
  JobApplicationFormZod,
  DualImageTextSectionZod,
  IllustrationZod,
]);

export type BlockType = z.infer<typeof blocksSchemaZod>;

function blockSupportsWaveWrapper(block: BlockType): boolean {
  return 'waveWrapper' in block;
}

/** Blocs sans `waveWrapper` dans le schéma : pas de chevauchement automatique */
function blockHasWaveWrapper(block: BlockType): boolean {
  if (!('waveWrapper' in block)) return false;
  return Boolean((block as { waveWrapper?: unknown }).waveWrapper);
}

function waveFromBlock(block: BlockType): WaveWrapperType | null {
  if (!('waveWrapper' in block)) return null;
  return (block as { waveWrapper: WaveWrapperType | null }).waveWrapper;
}

/** Layout éditorial du bloc (ex. text-media) pour `WaveWrapper`, si présent sur le schéma. */
function blockLayoutForWaveWrapper(block: BlockType): 'default' | 'reverse' | undefined {
  if (!('layout' in block)) return undefined;
  const { layout } = block as { layout?: unknown };
  if (layout !== 'par défaut' && layout !== 'inversé') return undefined;
  return mapStrapiLayoutToWaveWrapper(layout);
}

function renderBlockComponent(block: BlockType) {
  const componentType = block.__component;

  switch (componentType) {
  case BlockNameEnum.HERO_SECTION:
    return <HeroSection {...block} />;
  case BlockNameEnum.TEXT_BUBBLES:
    return <TextBubbles {...block} />;
  case BlockNameEnum.LOGO_CLIENTS:
    return <LogoClients {...block} className="section-gap" />;
  case BlockNameEnum.TEXT_MEDIA:
    return <TextMedia {...block} />;
  case BlockNameEnum.TEXT_ACCORDION:
    return <TextAccordion {...block} />;
  case BlockNameEnum.NUMBERED_LIST:
    return <NumberedList {...block} />;
  case BlockNameEnum.FAQ:
    return <FAQ {...block} />;
  case BlockNameEnum.ACTIVITIES:
    return <Activities {...block} />;
  case BlockNameEnum.CLIENT_OVERVIEW:
    return <ClientOverview {...block} />;
  case BlockNameEnum.FRAMED_PICTURES:
    return <FramedPictures {...block} />;
  case BlockNameEnum.CUSTOMER_CASES:
    return <CustomerCases {...block} />;
  case BlockNameEnum.DOUDOU_LIST:
    return <DoudouOverview {...block} className="section-gap" />;
  case BlockNameEnum.MOCKUP:
    return <Mockup {...block} />;
  case BlockNameEnum.OFFERS:
    return <Offers {...block} className="section-gap" />;
  case BlockNameEnum.QUOTATION:
    return <Quotation {...block} className="section-gap" />;
  case BlockNameEnum.PRESS:
    return <Press {...block} />;
  case BlockNameEnum.WYSIWYG:
    return <Wysiwyg {...block} />;
  case BlockNameEnum.JOB_DETAILS:
    return <JobDetails {...block} />;
  case BlockNameEnum.FORM_OFFER:
    return <OfferForm {...block} />;
  case BlockNameEnum.FORM_JOB_APPLICATION:
    return <JobApplicationForm {...block} />;
  case BlockNameEnum.DUAL_IMAGE_TEXT_SECTION:
    return <DualImageTextSection {...block} />;
  case BlockNameEnum.ILLUSTRATION:
    return <Illustration {...block} />;
  default:
    return (
      <div className="container">
        <p>{`Bloc Inconnu (${componentType ?? 'undefined'})`}</p>
      </div>
    );
  }
}

interface PageBlocksProps {
  blocks: BlockType[];
}

export function PageBlocks({ blocks }: PageBlocksProps) {
  return (
    <>
      {blocks.map((block, index) => {
        const waveOverlapNext =
          blockHasWaveWrapper(block) && index < blocks.length - 1 && blockHasWaveWrapper(blocks[index + 1]);
        const waveOverlapPrev = index > 0 && blockHasWaveWrapper(blocks[index - 1]) && blockHasWaveWrapper(block);
        const content = renderBlockComponent(block);
        const key = `${block.__component}-${block.id ?? index}`;

        if (!blockSupportsWaveWrapper(block)) {
          return <Fragment key={key}>{content}</Fragment>;
        }

        const wave = waveFromBlock(block);
        const overlapClassName = cn([waveOverlapNext && 'waveOverlapNext', waveOverlapPrev && 'waveOverlapPrev']);

        return (
          <WaveWrapper
            key={key}
            active={!!wave}
            variant={wave?.variant}
            color={wave?.color}
            mode={wave?.mode}
            layout={blockLayoutForWaveWrapper(block)}
            blockName={block.__component}
            className={overlapClassName}
          >
            {content}
          </WaveWrapper>
        );
      })}
    </>
  );
}
